import { MoreVertical, Plus, X } from 'lucide-react';
import { PermissionGate } from '../../auth/PermissionGate';
import { Button } from '../Button';
import { CountBadge } from '../CountBadge';
import { Menu, type MenuItem } from '../Menu';
import { Tooltip } from '../Tooltip';

/**
 * The multi-select toolbar — reference §4: `[+] | [⋮] | [N selected] | [✕]`.
 *
 * Replaces the normal toolbar while anything is selected. It ships as a whole bar, not as
 * a checkbox column bolted onto the existing strip: mixing per-row and bulk affordances in
 * one row is how users end up applying a bulk action to the wrong set.
 */
export function IndexSelectionBar({
  module,
  selectedCount,
  bulkItems,
  onCreate,
  createPermission,
  createLabel = 'Create',
  createDisabledReason,
  createMenu,
  onClear,
}: {
  module: string;
  selectedCount: number;
  bulkItems: MenuItem[];
  onCreate?: () => void;
  createPermission?: string;
  /** Matches the toolbar's — one action in three places says one thing. */
  createLabel?: string;
  createDisabledReason?: string | null;
  createMenu?: MenuItem[];
  onClear: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-blue-50/60 px-4 py-2.5">
      {/*
        The same create affordance the toolbar carries, in the same shape. A module with more
        than one thing to create offers the menu here too: the bar REPLACES the toolbar while
        rows are selected, so an action available in one and missing from the other would
        appear and disappear as the reader ticks a box.
      */}
      {createMenu && createMenu.length > 0 ? (
        <PermissionGate permission={createPermission}>
          <Menu
            id={`${module}-selection-create-menu`}
            items={createMenu}
            align="left"
            trigger={({ toggle }) => (
              <Tooltip label={createLabel} disabledReason={createDisabledReason ?? null}>
                <Button
                  id={`${module}-selection-create`}
                  variant="primary"
                  onClick={toggle}
                  aria-label={createLabel}
                  disabled={Boolean(createDisabledReason?.trim())}
                  className="px-2.5"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                </Button>
              </Tooltip>
            )}
          />
        </PermissionGate>
      ) : onCreate ? (
        <PermissionGate permission={createPermission}>
          <Tooltip label={createLabel} disabledReason={createDisabledReason ?? null}>
            <Button
              id={`${module}-selection-create`}
              variant="primary"
              onClick={onCreate}
              aria-label={createLabel}
              disabled={Boolean(createDisabledReason?.trim())}
              className="px-2.5"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </Button>
          </Tooltip>
        </PermissionGate>
      ) : null}

      <Menu
        id={`${module}-bulk-menu`}
        align="left"
        items={bulkItems}
        trigger={({ open, toggle }) => (
          <Tooltip
            label="Bulk actions"
            disabledReason={bulkItems.length === 0 ? 'No bulk actions are available here.' : null}
          >
            <Button
              id={`${module}-bulk-actions`}
              // BRAND, as Create beside it — product call, 08 Oct 2026: what can be done to the selection
              // is the bar's primary affordance, not a secondary one.
              variant="primary"
              onClick={toggle}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label="Bulk actions"
              disabled={bulkItems.length === 0}
              className="px-2.5"
            >
              <MoreVertical className="h-4 w-4" aria-hidden="true" />
            </Button>
          </Tooltip>
        )}
      />

      <span className="flex flex-1 items-center text-xs text-slate-600">
        <CountBadge count={selectedCount} tone="blue" />
        <span className="ml-1.5">selected</span>
      </span>

      <Tooltip label="Clear selection">
        <Button
          id={`${module}-clear-selection`}
          variant="tertiary"
          onClick={onClear}
          aria-label="Clear selection"
          className="px-2.5"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </Tooltip>
    </div>
  );
}
